/**
 * calculator.ts - Backward-compatible scoring API
 * 
 * This file now serves as a facade for the UnifiedScoringService,
 * maintaining backward compatibility with existing code while using
 * the consolidated scoring implementation.
 */

import { Investment } from '../types/Investment';
import { IncidentResponse } from '../types/Incident';
import { incidents } from '../data/incidents';
import { UnifiedScoringService, ScoreCategory as ScoreCategoryType, InvestmentImpactDetails as InvestmentImpactDetailsType, ScoreBreakdown as ScoreBreakdownType } from '../services/UnifiedScoringService';
import { SynergyMapping } from '../types/ScenarioManagement';

// Re-export types from UnifiedScoringService
export type { 
  ScoreCategory, 
  ScoreBreakdown, 
  InvestmentImpactDetails,
  BusinessCriteria 
} from '../services/UnifiedScoringService';

// Legacy types for backward compatibility
export interface InvestmentEffect {
  categoryBonus: ScoreCategoryType;
  incidentMitigation: number;
  responseTimeImprovement: number;
}

export interface InvestmentSynergy {
  investmentId: string;
  incidentCategories: string[];
  bonusMultiplier: number;
  unlocksBetterOptions?: boolean;
  description: string;
}

export interface StrategyComparison {
  name: string;
  description?: string;
  investments: Investment[];
  cost: number;
  scoreImpact: ScoreCategoryType;
  effectiveness: number;
  score: number;
  highlight?: boolean;
}

// Get singleton instance
const scoringService = UnifiedScoringService.getInstance();

/**
 * Calculate investment bonus - backward compatible wrapper
 */
export function calculateInvestmentBonus(investments: Investment[]): ScoreCategoryType {
  const config = scoringService.getConfiguration() || scoringService['getDefaultConfiguration']();
  
  const bonus: ScoreCategoryType = {
    security: 0,
    reputation: 0,
    financial: 0,
    operational: 0
  };

  investments.forEach(investment => {
    const effect = config.investmentEffects[investment.id];
    if (effect) {
      bonus.security += effect.categoryBonuses.security;
      bonus.reputation += effect.categoryBonuses.reputation;
      bonus.financial += effect.categoryBonuses.financial;
      bonus.operational += effect.categoryBonuses.operational;
    }
  });

  return bonus;
}

/**
 * Calculate incident impact - backward compatible wrapper
 */
export function calculateIncidentImpact(
  responses: IncidentResponse[],
  investments: Investment[]
): { impact: ScoreCategoryType; investmentDetails: InvestmentImpactDetailsType[] } {
  // Use scoring service to calculate
  const result = scoringService.calculateScore(investments, responses, incidents);
  
  return {
    impact: result.incidentImpact,
    investmentDetails: result.investmentImpactDetails || []
  };
}

/**
 * Calculate response time bonus - DEPRECATED: Always returns 0
 * Time no longer affects scoring
 */
export function calculateResponseTimeBonus(
  responses: IncidentResponse[],
  investments: Investment[]
): number {
  // Time no longer affects scoring - always return 0
  return 0;
}

/**
 * Calculate overall score - backward compatible wrapper
 */
export function calculateOverallScore(scores: ScoreCategoryType): number {
  const config = scoringService.getConfiguration() || scoringService['getDefaultConfiguration']();
  const weights = config.categoryWeights;
  
  const weighted = 
    scores.security * weights.security +
    scores.reputation * weights.reputation +
    scores.financial * weights.financial +
    scores.operational * weights.operational;
  
  const totalWeight = weights.security + weights.reputation + weights.financial + weights.operational;
  
  const score = Math.round(weighted / totalWeight);
  return Math.max(0, Math.min(100, score)); // Clamp between 0 and 100
}

/**
 * Refresh grade thresholds - now a no-op as UnifiedScoringService handles this
 */
export function refreshGradeThresholds(): void {
  // No-op - UnifiedScoringService handles configuration
}

/**
 * Get grade - backward compatible wrapper
 */
export function getGrade(score: number): string {
  const config = scoringService.getConfiguration() || scoringService['getDefaultConfiguration']();
  const thresholds = config.gradeThresholds;
  
  const sortedThresholds = [...thresholds]
    .sort((a, b) => b.minScore - a.minScore);
  
  for (const threshold of sortedThresholds) {
    if (score >= threshold.minScore) {
      return threshold.grade;
    }
  }
  
  return 'F';
}

/**
 * Get performance description - backward compatible wrapper
 */
export function getPerformanceDescription(score: number): string {
  const grade = getGrade(score);
  
  // Map grades to performance descriptions
  const gradeToDescription: Record<string, string> = {
    'EXCELLENT': 'Outstanding response',
    'WISE': 'Smart decision making', 
    'DECENT': 'Reasonable response',
    'OK': 'Acceptable response',
    'POOR': 'Suboptimal response',
    'F': 'Suboptimal response'
  };
  
  return gradeToDescription[grade] || 'Suboptimal response';
}

/**
 * Calculate complete score - backward compatible wrapper
 */
export function calculateCompleteScore(
  investments: Investment[],
  incidentResponses: IncidentResponse[],
  scenarioSynergies?: SynergyMapping[]
): ScoreBreakdownType {
  // Use scoring service
  const result = scoringService.calculateScore(investments, incidentResponses, incidents, scenarioSynergies);
  
  // Map to old ScoreBreakdown format
  return {
    baseScore: result.baseScore,
    investmentBonus: result.investmentBonus,
    incidentImpact: result.incidentImpact,
    responseTimeBonus: result.responseTimeBonus,
    finalScore: result.finalScore,
    overallScore: result.overallScore,
    grade: result.grade,
    performance: result.performance,
    investmentImpactDetails: result.investmentImpactDetails
  };
}

/**
 * Calculate strategy comparisons - backward compatible wrapper
 */
export function calculateStrategyComparisons(
  currentInvestments: Investment[],
  allInvestments: Investment[],
  incidentResponses: IncidentResponse[]
): StrategyComparison[] {
  const strategies: StrategyComparison[] = [];
  
  // Current strategy
  const currentScore = scoringService.calculateScore(currentInvestments, incidentResponses, incidents);
  strategies.push({
    name: 'Your Strategy',
    description: 'The investment strategy you selected',
    investments: currentInvestments,
    cost: currentInvestments.reduce((sum, inv) => sum + inv.cost, 0),
    scoreImpact: currentScore.finalScore,
    effectiveness: currentScore.overallScore,
    score: currentScore.overallScore,
    highlight: true
  });
  
  // Alternative strategies
  const alternativeStrategies = [
    {
      name: 'Security-Focused',
      description: 'Prioritizes technical security controls',
      investmentIds: ['security-monitoring', 'access-control', 'encryption', 'penetration-testing']
    },
    {
      name: 'Compliance-Focused',
      description: 'Emphasizes regulatory compliance and governance',
      investmentIds: ['compliance-program', 'vendor-management', 'dlp-solution', 'security-awareness']
    },
    {
      name: 'Resilience-Focused',
      description: 'Focuses on recovery and incident response',
      investmentIds: ['backup-recovery', 'incident-response-team', 'cyber-insurance', 'forensics-capability']
    },
    {
      name: 'Balanced Approach',
      description: 'Covers all critical areas with moderate investment',
      investmentIds: ['security-training', 'security-monitoring', 'backup-recovery', 'compliance-program']
    }
  ];
  
  alternativeStrategies.forEach(strategy => {
    const strategyInvestments = allInvestments.filter(inv => 
      strategy.investmentIds.includes(inv.id)
    );
    const strategyScore = scoringService.calculateScore(strategyInvestments, incidentResponses, incidents);
    
    strategies.push({
      name: strategy.name,
      description: strategy.description,
      investments: strategyInvestments,
      cost: strategyInvestments.reduce((sum, inv) => sum + inv.cost, 0),
      scoreImpact: strategyScore.finalScore,
      effectiveness: strategyScore.overallScore,
      score: strategyScore.overallScore
    });
  });
  
  return strategies;
}

/**
 * Calculate response preview - backward compatible wrapper
 */
export function calculateResponsePreview(
  optionId: string,
  incidentId: string,
  responseTime: number,
  currentInvestments: Investment[],
  scenarioSynergies?: SynergyMapping[]
): Partial<ScoreBreakdownType> {
  const incident = incidents.find(i => i.id === incidentId);
  if (!incident) {
    return {};
  }

  return scoringService.calculateIncidentPreview(
    optionId,
    incidentId,
    responseTime,
    currentInvestments,
    incident,
    scenarioSynergies
  );
}